import { Award, Shield, Clock, Users, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AboutSection() {
  const credentials = [
    {
      icon: Award,
      title: "ISA Certified Arborists",
      description: "International Society of Arboriculture certified",
    },
    {
      icon: Shield,
      title: "Fully Licensed & Insured",
      description: "Complete coverage for your peace of mind",
    },
    {
      icon: Clock,
      title: "Next-Day Appointments",
      description: "ISA Arborist assessments available next business day",
    },
    {
      icon: Users,
      title: "Expert Team",
      description: "Skilled professionals with extensive expertise",
    },
  ];

  return (
    <section
      id="about"
      className="py-28 lg:py-36 bg-white"
      data-testid="about-section"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Content */}
          <div>
            <p className="font-['Cinzel'] text-[#10B981] text-xs tracking-[0.3em] uppercase mb-4 text-center">
              The Gold Standard in Arboriculture
            </p>

            <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[#0A1628] mb-6 leading-tight text-center">
              Why Paradise Valley Trusts
              <br />
              <span className="gradient-text">Celebrity Tree</span>
            </h2>

            <p className="text-[#64748B] text-lg mb-10 leading-relaxed text-center max-w-3xl mx-auto">
              Since 2015, Celebrity Tree Service LLC has been the
              trusted choice for Arizona's most prestigious properties. Our ISA
              Certified Arborists combine scientific expertise with old-world
              craftsmanship to deliver unparalleled tree care.
            </p>

            {/* Credentials Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
              {credentials.map((credential, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-5 bg-[#F8F6F1] border-l-4 border-[#10B981] hover:shadow-lg hover:shadow-emerald-500/5 transition-shadow"
                >
                  <div className="w-10 h-10 bg-[#10B981]/10 flex items-center justify-center flex-shrink-0">
                    <credential.icon className="w-5 h-5 text-[#10B981]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0A1628] mb-1 text-sm">
                      {credential.title}
                    </h3>
                    <p className="text-[#64748B] text-xs">
                      {credential.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Key Differentiators */}
            <div className="bg-[#F8F6F1] p-6 mb-10">
              <h3 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#0A1628] mb-4">
                What Sets Us Apart
              </h3>
              <div className="space-y-3">
                {[
                  "Locally owned and operated in Arizona",
                  "Every job supervised by ISA Certified Arborist",
                  "Specialized in luxury estate and golf course properties",
                  "Transparent pricing with no hidden fees",
                  "24/7 emergency response during monsoon season",
                ].map((point, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0" />
                    <span className="text-[#64748B]">{point}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Arizona Pride */}
            <div className="flex items-center gap-5 mb-10 p-5 bg-gradient-to-r from-[#10B981]/10 to-transparent border-l-4 border-[#D4A853]">
              {/* Arizona State Flag */}
              <svg
                viewBox="0 0 60 40"
                className="w-16 h-11 flex-shrink-0"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="60" height="40" fill="#002868"/>
                <polygon points="0,0 30,20 60,0" fill="#BF0A30"/>
                <g fill="#FED700">
                  <polygon points="30,20 26,9 34,9"/>
                  <polygon points="30,20 22,12 38,12"/>
                  <polygon points="30,20 18,8 42,8"/>
                  <polygon points="30,20 16,6 44,6"/>
                </g>
                <circle cx="30" cy="20" r="5" fill="#FED700"/>
              </svg>
              <div>
                <p className="font-bold text-[#0A1628] text-lg">Locally Owned & Operated</p>
                <p className="text-[#64748B] text-sm">Proudly serving Paradise Valley & Scottsdale since 2015</p>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              <Button
                asChild
                className="bg-gradient-to-r from-[#10B981] to-[#059669] text-white rounded-none px-10 py-5 uppercase tracking-[0.15em] text-sm font-bold shadow-lg shadow-emerald-500/20"
                data-testid="about-cta"
              >
                <a href="#contact">Schedule Your Assessment</a>
              </Button>
            </div>
          </div>
      </div>
    </section>
  );
}
